<script setup>
// Auditoria → Exibição. A plataforma SEMPRE registra todos os eventos
// (gravação incondicional - ver AuditLog / Audit::Writer).
// Esta tela controla APENAS quais eventos APARECEM na listagem de auditoria
// (preferência de exibição, persistida em Account#audit_settings).
// NUNCA afeta o que é gravado.
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const currentAccount = useMapGetter('getCurrentAccount');

// Espelha Account::AUDIT_AUDITABLE_TYPES no backend.
const ENTITIES = [
  { key: 'User', label: 'Usuários', icon: 'i-lucide-user' },
  { key: 'AccountUser', label: 'Vínculos de conta', icon: 'i-lucide-user-cog' },
  { key: 'Account', label: 'Conta', icon: 'i-lucide-building' },
  { key: 'Inbox', label: 'Caixas de entrada', icon: 'i-lucide-inbox' },
  { key: 'InboxMember', label: 'Membros de caixa', icon: 'i-lucide-users' },
  { key: 'Team', label: 'Times', icon: 'i-lucide-users-round' },
  { key: 'TeamMember', label: 'Membros de time', icon: 'i-lucide-user-plus' },
  { key: 'Macro', label: 'Macros', icon: 'i-lucide-zap' },
  { key: 'AgentBot', label: 'Bots', icon: 'i-lucide-bot' },
  { key: 'AutomationRule', label: 'Automações', icon: 'i-lucide-workflow' },
  {
    key: 'Conversation',
    label: 'Conversas',
    icon: 'i-lucide-message-square',
    hint: 'Alto volume - oculto por padrão na listagem (o registro continua sendo feito).',
  },
  { key: 'Webhook', label: 'Webhooks', icon: 'i-lucide-webhook' },
  { key: 'Lead', label: 'Leads (CRM)', icon: 'i-lucide-target' },
  { key: 'LeadFollowUp', label: 'Follow-ups', icon: 'i-lucide-list-checks' },
  { key: 'Pipeline', label: 'Pipelines', icon: 'i-lucide-kanban' },
  { key: 'Stage', label: 'Estágios', icon: 'i-lucide-layers' },
  { key: 'FollowUpTemplate', label: 'Modelos de follow-up', icon: 'i-lucide-list-todo' },
];

const ACTIONS = [
  { key: 'create', label: 'Criação', icon: 'i-lucide-plus-circle', color: 'text-n-teal-11' },
  { key: 'update', label: 'Edição', icon: 'i-lucide-pen-square', color: 'text-n-amber-11' },
  { key: 'destroy', label: 'Exclusão', icon: 'i-lucide-trash-2', color: 'text-n-ruby-11' },
];

const draft = ref({ entities: {}, actions: {} });
const isSaving = ref(false);

function hydrate() {
  const cfg = currentAccount.value?.audit_settings || {};
  draft.value.entities = {};
  ENTITIES.forEach(e => {
    const v = cfg.entities?.[e.key];
    // Default de exibição: tudo visível, exceto Conversation (alto volume).
    draft.value.entities[e.key] = v === undefined ? e.key !== 'Conversation' : v !== false;
  });
  draft.value.actions = {};
  ACTIONS.forEach(a => {
    draft.value.actions[a.key] = cfg.actions?.[a.key] !== false;
  });
}

onMounted(hydrate);

const shownEntitiesCount = computed(
  () => ENTITIES.filter(e => draft.value.entities[e.key]).length
);
const shownActionsCount = computed(
  () => ACTIONS.filter(a => draft.value.actions[a.key]).length
);

function toggleEntity(key) {
  draft.value.entities[key] = !draft.value.entities[key];
}
function toggleAction(key) {
  draft.value.actions[key] = !draft.value.actions[key];
}
function showAll() {
  ENTITIES.forEach(e => {
    draft.value.entities[e.key] = true;
  });
  ACTIONS.forEach(a => {
    draft.value.actions[a.key] = true;
  });
}
function hideAll() {
  ENTITIES.forEach(e => {
    draft.value.entities[e.key] = false;
  });
}

async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    // Só preferência de exibição - entities/actions. A gravação é incondicional.
    await store.dispatch('accounts/update', {
      audit_settings: {
        entities: draft.value.entities,
        actions: draft.value.actions,
      },
    });
    useAlert('Preferências de exibição salvas.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Auditoria · Exibição"
        description="A plataforma registra TODOS os eventos sempre - isto não pode ser desligado. Aqui você escolhe apenas o que APARECE na listagem de auditoria."
        feature-name="audit_indicators"
      >
        <template #actions>
          <router-link
            :to="{ name: 'auditlogs_summary' }"
            class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
          >
            <i class="i-lucide-bar-chart-3 size-3.5" />
            Resumo
          </router-link>
          <router-link
            :to="{ name: 'auditlogs_list' }"
            class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
          >
            <i class="i-lucide-list size-3.5" />
            Ver eventos
          </router-link>
          <NextButton faded slate size="sm" label="Mostrar tudo" icon="i-lucide-eye" @click="showAll" />
          <NextButton faded slate size="sm" label="Ocultar tudo" icon="i-lucide-eye-off" @click="hideAll" />
          <NextButton
            size="sm"
            label="Salvar"
            icon="i-lucide-save"
            :is-loading="isSaving"
            @click="save"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-5 pt-2 pb-10">
        <!-- Aviso: gravação é sempre feita; aqui só exibição -->
        <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4 flex items-start gap-3">
          <i class="i-lucide-shield-check size-5 text-n-teal-11 shrink-0 mt-0.5" />
          <div class="min-w-0">
            <p class="text-heading-3 text-n-slate-12 mb-1">A auditoria é sempre registrada</p>
            <p class="text-body-small text-n-slate-11 mb-0">
              Todos os eventos da plataforma são gravados de forma incondicional -
              não há como desligar o registro. As opções abaixo controlam apenas
              quais eventos aparecem nesta listagem.
            </p>
          </div>
        </section>

        <!-- Ações exibidas -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between mb-3">
            <div>
              <p class="text-heading-3 text-n-slate-12 mb-0.5">Ações exibidas</p>
              <p class="text-body-small text-n-slate-11 mb-0">
                {{ shownActionsCount }} de {{ ACTIONS.length }} visíveis
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <button
              v-for="a in ACTIONS"
              :key="a.key"
              type="button"
              class="text-left p-3 rounded-xl border-2 transition-colors flex items-center gap-3"
              :class="
                draft.actions[a.key]
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-7 opacity-70'
              "
              @click="toggleAction(a.key)"
            >
              <span class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0">
                <i :class="[a.icon, a.color]" class="size-4" />
              </span>
              <div class="flex-1">
                <p class="text-body-main font-semibold text-n-slate-12 mb-0">{{ a.label }}</p>
              </div>
              <i
                :class="draft.actions[a.key] ? 'i-lucide-eye text-n-brand' : 'i-lucide-eye-off text-n-slate-10'"
                class="size-4 shrink-0"
              />
            </button>
          </div>
        </section>

        <!-- Entidades exibidas -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between mb-3">
            <div>
              <p class="text-heading-3 text-n-slate-12 mb-0.5">Entidades exibidas</p>
              <p class="text-body-small text-n-slate-11 mb-0">
                {{ shownEntitiesCount }} de {{ ENTITIES.length }} visíveis
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <button
              v-for="e in ENTITIES"
              :key="e.key"
              type="button"
              class="text-left p-3 rounded-xl border-2 transition-colors flex items-start gap-3"
              :class="
                draft.entities[e.key]
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-7 opacity-70'
              "
              @click="toggleEntity(e.key)"
            >
              <span class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0">
                <i :class="e.icon" class="size-4 text-n-slate-12" />
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-body-main font-semibold text-n-slate-12 mb-0 truncate">{{ e.label }}</p>
                <p class="text-label-small text-n-slate-11 mb-0">{{ e.key }}</p>
                <p v-if="e.hint" class="text-label-small text-n-amber-11 mt-1 mb-0">{{ e.hint }}</p>
              </div>
              <i
                :class="draft.entities[e.key] ? 'i-lucide-eye text-n-brand' : 'i-lucide-eye-off text-n-slate-10'"
                class="size-4 shrink-0 mt-0.5"
              />
            </button>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
